// "New training available" alert state. Watches the live, in-scope modules and surfaces the
// most recently gone-live one (its begins_at has passed) when it is newer than what the user
// last dismissed — so scheduled releases trigger the alert automatically at go-live. Dismissal
// is remembered per rooftop in localStorage; alerts also self-expire after a max lifespan so a
// module that's been live for weeks doesn't keep nagging.

import { useCallback, useReducer, useState } from 'react';
import { useRooftop } from '@store';
import { RECENT_MODULE_WINDOW_MS } from '../utils/recency';
import type { ModuleSummary } from '../types';

// A module stops nagging once it's been live longer than the "recently released" window (shared
// with the library's "New" card badge so the banner and the badge agree).
const MAX_LIFESPAN_MS = RECENT_MODULE_WINDOW_MS;
const storageKeyFor = (rooftopId?: number | string | null) =>
  `lc_new_content_dismissed_${rooftopId ?? 'global'}`;

function readDismissed(key: string): number {
  try {
    const v = localStorage.getItem(key);
    if (!v) return 0;
    // A non-ISO / corrupt value yields NaN; `newestMs > NaN` is always false, which would
    // permanently suppress the alert. Treat an unparseable value as "never dismissed".
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
  } catch {
    return 0;
  }
}

export function useNewContentAlert(modules: ModuleSummary[]) {
  const rooftop = useRooftop();
  const storageKey = storageKeyFor(rooftop?.id);
  const [, bump] = useReducer((x: number) => x + 1, 0);

  // Snapshot once per mount: the go-live / lifespan windows are hours-to-days wide, so a stable
  // "now" is plenty precise — and it keeps render pure/idempotent (react-compiler purity rule).
  const [now] = useState(() => Date.now());
  const dismissedAt = readDismissed(storageKey);

  // Newest module that has actually gone live (begins_at <= now).
  let newest: ModuleSummary | null = null;
  let newestMs = 0;
  for (const m of modules) {
    const t = new Date(m.begins_at).getTime();
    if (t <= now && t > newestMs) {
      newestMs = t;
      newest = m;
    }
  }

  const withinLifespan = newestMs > 0 && now - newestMs <= MAX_LIFESPAN_MS;
  const isNew = newestMs > dismissedAt;
  const alertModule = newest && withinLifespan && isNew ? newest : null;

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(storageKey, new Date().toISOString());
    } catch {
      /* ignore storage failures */
    }
    bump();
  }, [storageKey, bump]);

  return { alertModule, dismiss };
}
